import { useEffect, useMemo, useRef, useState } from 'react';
import Avatar from '../../components/Avatar.jsx';
import {
  Plus,
  Archive,
  ArchiveRestore,
  Trash2,
  Edit3,
  ChevronDown,
  ChevronRight,
  X,
} from 'lucide-react';

/* ============================================================
   línkè · 对话线下拉抽屉
   - 顶部 "+ 新对话" 按钮
   - 非归档对话列表（按 updated_at 倒序，后端已排）
   - 已归档分组（折叠，默认收起）
   - 每条：角色卡名 / 自定义名 / 消息预览 / 时间；右侧 "..." 操作
   - 改名 用 inline input；归档/恢复/删除是按钮
   ============================================================ */

function relTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60 * 1000) return '刚刚';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)} 分前`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)} 小时前`;
  if (diff < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / 86400000)} 天前`;
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export default function ConversationsDrawer({
  conversations,
  currentConvId,
  characters,
  generating,
  onSwitch,
  onCreate,
  onRename,
  onArchive,
  onDelete,
  onClose,
}) {
  const overlayRef = useRef(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [actionOpenId, setActionOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  // 点击 overlay 自身（非内容）关闭
  function onOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }
  // Esc 关闭
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 背景滚动锁：新布局下 document 自身不滚动（body overflow:hidden，滚动都在
  // .ch-scroll 内部），且 .cd-overlay 是全屏 fixed 遮罩、已挡住背后所有触摸，
  // 所以不再需要旧的 body position:fixed 法（那正是会跟 iOS 视口打架的写法）。

  const active = useMemo(
    () => conversations.filter((c) => !c.archived),
    [conversations],
  );
  const archived = useMemo(
    () => conversations.filter((c) => c.archived),
    [conversations],
  );

  function startEdit(c) {
    setEditingId(c.id);
    setEditValue(c.name);
    setActionOpenId(null);
  }
  async function commitEdit(c) {
    const v = (editValue || '').trim();
    setEditingId(null);
    if (!v || v === c.name) return;
    await onRename(c.id, v);
  }
  function cancelEdit() {
    setEditingId(null);
  }

  function renderItem(c) {
    const char = characters.find((x) => x.id === c.character_id);
    const isCurrent = c.id === currentConvId;
    const isEditing = editingId === c.id;
    const isActionOpen = actionOpenId === c.id;
    return (
      <div
        key={c.id}
        className={'cd-item' + (isCurrent ? ' on' : '') + (c.archived ? ' archived' : '')}
      >
        <button
          className="cd-item-main"
          onClick={() => !isEditing && onSwitch(c.id)}
          disabled={generating || isEditing}
        >
          <Avatar who="sael" className="cd-avatar" />
          <div className="cd-text">
            {isEditing ? (
              <input
                className="cd-rename-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => commitEdit(c)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitEdit(c);
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEdit();
                  }
                }}
                autoFocus
              />
            ) : (
              <>
                <div className="cd-name">{c.name}</div>
                <div className="cd-meta">
                  <span className="cd-char">{char ? char.name : '· 无角色卡 ·'}</span>
                  <span className="cd-dot" aria-hidden="true">·</span>
                  <span className="cd-count">{c.msg_count} 条</span>
                  <span className="cd-dot" aria-hidden="true">·</span>
                  <span className="cd-time">{relTime(c.updated_at)}</span>
                </div>
                {c.last_preview && (
                  <div className="cd-preview">{c.last_preview}</div>
                )}
              </>
            )}
          </div>
        </button>
        {!isEditing && (
          <div className="cd-item-actions">
            <button
              className={'cd-action-toggle' + (isActionOpen ? ' on' : '')}
              onClick={() => setActionOpenId(isActionOpen ? null : c.id)}
              aria-label="操作"
              title="操作"
            >
              <span>···</span>
            </button>
            {isActionOpen && (
              <div className="cd-action-menu">
                <button onClick={() => startEdit(c)}>
                  <Edit3 size={11} strokeWidth={1.7} /> 改名
                </button>
                <button
                  onClick={() => {
                    setActionOpenId(null);
                    onArchive(c.id, !c.archived);
                  }}
                >
                  {c.archived ? (
                    <>
                      <ArchiveRestore size={11} strokeWidth={1.7} /> 恢复
                    </>
                  ) : (
                    <>
                      <Archive size={11} strokeWidth={1.7} /> 归档
                    </>
                  )}
                </button>
                <button
                  className="cd-danger"
                  onClick={() => {
                    setActionOpenId(null);
                    onDelete(c.id);
                  }}
                >
                  <Trash2 size={11} strokeWidth={1.7} /> 删除
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="cd-overlay" ref={overlayRef} onClick={onOverlayClick}>
      <div className="cd-panel">
        <div className="cd-panel-head">
          <span className="cd-panel-title">对 话</span>
          <button className="cd-close" onClick={onClose} aria-label="关闭">
            <X size={14} strokeWidth={1.8} />
          </button>
        </div>
        <button
          className="cd-create"
          onClick={onCreate}
          disabled={generating}
          title="新建一条对话（用当前默认角色卡）"
        >
          <Plus size={14} strokeWidth={1.8} /> 新 对 话
        </button>
        <div className="cd-list">
          {active.length === 0 && archived.length === 0 && (
            <div className="cd-empty">还没有对话 · 点上面 "新对话"</div>
          )}
          {active.map(renderItem)}
          {archived.length > 0 && (
            <div className="cd-archived-section">
              <button
                className="cd-archived-head"
                onClick={() => setArchivedOpen((v) => !v)}
              >
                {archivedOpen ? (
                  <ChevronDown size={12} strokeWidth={1.8} />
                ) : (
                  <ChevronRight size={12} strokeWidth={1.8} />
                )}
                <span>已归档 · {archived.length}</span>
              </button>
              {archivedOpen && archived.map(renderItem)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
